import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { GET_EVENTS, GET_SERVICES, GET_TESTIMONIALS } from "../graphql/queries";
import {
  CREATE_EVENT,
  CREATE_SERVICE,
  CREATE_TESTIMONIAL,
  DELETE_EVENT,
  DELETE_SERVICE,
  DELETE_TESTIMONIAL,
  UPDATE_EVENT,
  UPDATE_SERVICE,
  UPDATE_TESTIMONIAL,
} from "../graphql/mutations";
import { Event, Service, Testimonial } from "../types";

interface AdminData {
  events: Event[];
  services: Service[];
  testimonials: Testimonial[];
}

interface AdminDataContextValue extends AdminData {
  loading: boolean;
  error: string | null;
  mutationError: string | null;
  addEvent: (event: Omit<Event, "id">) => Promise<void>;
  updateEvent: (id: string, event: Omit<Event, "id">) => Promise<void>;
  removeEvent: (id: string) => Promise<void>;
  addService: (service: Omit<Service, "id">) => Promise<void>;
  updateService: (id: string, service: Omit<Service, "id">) => Promise<void>;
  removeService: (id: string) => Promise<void>;
  addTestimonial: (testimonial: Omit<Testimonial, "id">) => Promise<void>;
  updateTestimonial: (id: string, testimonial: Omit<Testimonial, "id">) => Promise<void>;
  removeTestimonial: (id: string) => Promise<void>;
  resetToServerData: () => Promise<void>;
}

const AdminDataContext = createContext<AdminDataContextValue | null>(null);

function toEventInput(event: Omit<Event, "id">) {
  return {
    title: event.title,
    eventType: event.eventType,
    date: event.date,
    location: event.location,
    description: event.description,
    heroImageUrl: event.heroImageUrl,
    galleryImageUrls: event.galleryImageUrls ?? [],
  };
}

function toServiceInput(service: Omit<Service, "id">) {
  return {
    name: service.name,
    description: service.description,
    priceRange: service.priceRange,
    imageUrl: service.imageUrl,
  };
}

function toTestimonialInput(testimonial: Omit<Testimonial, "id">) {
  return {
    clientName: testimonial.clientName,
    quote: testimonial.quote,
    eventType: testimonial.eventType,
    rating: testimonial.rating,
  };
}

function AdminDataProvider({ children }: { children: React.ReactNode }) {
  const {
    data: eventsData,
    loading: eventsLoading,
    error: eventsError,
    refetch: refetchEvents,
  } = useQuery<{ events: Event[] }>(GET_EVENTS);
  const {
    data: servicesData,
    loading: servicesLoading,
    error: servicesError,
    refetch: refetchServices,
  } = useQuery<{ services: Service[] }>(GET_SERVICES);
  const {
    data: testimonialsData,
    loading: testimonialsLoading,
    error: testimonialsError,
    refetch: refetchTestimonials,
  } = useQuery<{ testimonials: Testimonial[] }>(GET_TESTIMONIALS);

  const [mutationError, setMutationError] = useState<string | null>(null);

  const [createEventMutation] = useMutation(CREATE_EVENT, { refetchQueries: [{ query: GET_EVENTS }] });
  const [updateEventMutation] = useMutation(UPDATE_EVENT, { refetchQueries: [{ query: GET_EVENTS }] });
  const [deleteEventMutation] = useMutation(DELETE_EVENT, { refetchQueries: [{ query: GET_EVENTS }] });

  const [createServiceMutation] = useMutation(CREATE_SERVICE, {
    refetchQueries: [{ query: GET_SERVICES }],
  });
  const [updateServiceMutation] = useMutation(UPDATE_SERVICE, {
    refetchQueries: [{ query: GET_SERVICES }],
  });
  const [deleteServiceMutation] = useMutation(DELETE_SERVICE, {
    refetchQueries: [{ query: GET_SERVICES }],
  });

  const [createTestimonialMutation] = useMutation(CREATE_TESTIMONIAL, {
    refetchQueries: [{ query: GET_TESTIMONIALS }],
  });
  const [updateTestimonialMutation] = useMutation(UPDATE_TESTIMONIAL, {
    refetchQueries: [{ query: GET_TESTIMONIALS }],
  });
  const [deleteTestimonialMutation] = useMutation(DELETE_TESTIMONIAL, {
    refetchQueries: [{ query: GET_TESTIMONIALS }],
  });

  const events = eventsData?.events ?? [];
  const services = servicesData?.services ?? [];
  const testimonials = testimonialsData?.testimonials ?? [];

  useEffect(() => {
    localStorage.removeItem("pbj-admin-data");
  }, []);

  const runMutation = useCallback(async (action: () => Promise<unknown>) => {
    setMutationError(null);
    try {
      await action();
    } catch (err) {
      const message = err instanceof Error ? err.message : "Save failed";
      setMutationError(message);
      throw err;
    }
  }, []);

  const addEvent = useCallback(
    async (event: Omit<Event, "id">) => {
      await runMutation(() =>
        createEventMutation({ variables: { input: toEventInput(event) } })
      );
    },
    [createEventMutation, runMutation]
  );

  const updateEvent = useCallback(
    async (id: string, event: Omit<Event, "id">) => {
      await runMutation(() =>
        updateEventMutation({ variables: { id, input: toEventInput(event) } })
      );
    },
    [updateEventMutation, runMutation]
  );

  const removeEvent = useCallback(
    async (id: string) => {
      await runMutation(() => deleteEventMutation({ variables: { id } }));
    },
    [deleteEventMutation, runMutation]
  );

  const addService = useCallback(
    async (service: Omit<Service, "id">) => {
      await runMutation(() =>
        createServiceMutation({ variables: { input: toServiceInput(service) } })
      );
    },
    [createServiceMutation, runMutation]
  );

  const updateService = useCallback(
    async (id: string, service: Omit<Service, "id">) => {
      await runMutation(() =>
        updateServiceMutation({ variables: { id, input: toServiceInput(service) } })
      );
    },
    [updateServiceMutation, runMutation]
  );

  const removeService = useCallback(
    async (id: string) => {
      await runMutation(() => deleteServiceMutation({ variables: { id } }));
    },
    [deleteServiceMutation, runMutation]
  );

  const addTestimonial = useCallback(
    async (testimonial: Omit<Testimonial, "id">) => {
      await runMutation(() =>
        createTestimonialMutation({ variables: { input: toTestimonialInput(testimonial) } })
      );
    },
    [createTestimonialMutation, runMutation]
  );

  const updateTestimonial = useCallback(
    async (id: string, testimonial: Omit<Testimonial, "id">) => {
      await runMutation(() =>
        updateTestimonialMutation({
          variables: { id, input: toTestimonialInput(testimonial) },
        })
      );
    },
    [updateTestimonialMutation, runMutation]
  );

  const removeTestimonial = useCallback(
    async (id: string) => {
      await runMutation(() => deleteTestimonialMutation({ variables: { id } }));
    },
    [deleteTestimonialMutation, runMutation]
  );

  const resetToServerData = useCallback(async () => {
    setMutationError(null);
    await Promise.all([refetchEvents(), refetchServices(), refetchTestimonials()]);
  }, [refetchEvents, refetchServices, refetchTestimonials]);

  const loading = eventsLoading || servicesLoading || testimonialsLoading;
  const error =
    eventsError?.message ?? servicesError?.message ?? testimonialsError?.message ?? null;

  const value = useMemo(
    () => ({
      events,
      services,
      testimonials,
      loading,
      error,
      mutationError,
      addEvent,
      updateEvent,
      removeEvent,
      addService,
      updateService,
      removeService,
      addTestimonial,
      updateTestimonial,
      removeTestimonial,
      resetToServerData,
    }),
    [
      events,
      services,
      testimonials,
      loading,
      error,
      mutationError,
      addEvent,
      updateEvent,
      removeEvent,
      addService,
      updateService,
      removeService,
      addTestimonial,
      updateTestimonial,
      removeTestimonial,
      resetToServerData,
    ]
  );

  return <AdminDataContext.Provider value={value}>{children}</AdminDataContext.Provider>;
}

function useAdminData() {
  const context = useContext(AdminDataContext);
  if (!context) {
    throw new Error("useAdminData must be used within AdminDataProvider");
  }
  return context;
}

export { AdminDataProvider, useAdminData };
